// Libraries
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Flex, Text } from '@chakra-ui/react';
import Link from 'next/link';
// Types
import { BreadcrumbItemProps } from './BreadcrumbItem.type';

/**
 * A component representing an individual breadcrumb item within the Breadcrumb component.
 *
 * @component
 * @param {BreadcrumbItemProps} props - The props for the BreadcrumbItem component.
 * @param {string} props.title - The title of the breadcrumb item (optional).
 * @param {string} [props.text=''] - The text content of the breadcrumb item (optional).
 * @param {string} [props.href=''] - The link URL for the breadcrumb item (optional).
 * @param {boolean} [props.isCurrentPage=false] - A flag indicating if the breadcrumb item represents the current page (optional).
 * @param {boolean} [props.withCaret=false] - A flag indicating if the breadcrumb item should have a caret icon (optional).
 * @returns {ReactElement} The BreadcrumbItem component.
 */
export const BreadcrumbItem = (props: BreadcrumbItemProps) => {
  const { title, text = '', href = '', isCurrentPage = false, withCaret = false } = props;

  const border = {
    content: '""',
    position: 'absolute',
    bottom: 0,
    visibility: 'visible',
    height: '3px',
    width: 'full',
    backgroundColor: 'base',
  };

  const hoverColor = !isCurrentPage ? 'base' : undefined;
  const borderVisibility = isCurrentPage ? 'visible' : 'hidden';
  const borderColor = isCurrentPage ? 'base' : 'grey.2';

  return (
    <Flex as='li' h='full' justify='center' alignContent='center'>
      {title && (
        <Text color='white' fontSize='md' fontWeight='medium' mr={2} my='auto'>
          {title}
        </Text>
      )}
      <Flex
        h='full'
        position='relative'
        _after={{ ...border, visibility: borderVisibility }}
        _hover={{ color: hoverColor, _after: { ...border, backgroundColor: borderColor } }}
      >
        {isCurrentPage ? (
          <Box as='span' my='auto' data-testid='breadcrumb-current'>
            {text}
          </Box>
        ) : (
          <Box as={Link} my='auto' href={href} data-testid='breadcrumb-link-previous'>
            {text}
          </Box>
        )}
      </Flex>
      {withCaret && <ChevronRightIcon w={6} h={6} my='auto' />}
    </Flex>
  );
};
